import * as PIXI from 'pixi.js';
import { useGameStore } from './gameStore.js';
import { CELL_SIZE } from './constants.js';

const DRAG_THRESHOLD = 10; // px minimum movement before drag starts

export class GameController {
  constructor(store, renderer) {
    this.store = store;
    this.renderer = renderer;
    this.battleInterval = null;
    this.manaInterval = null;
    this.timeInterval = null;
    this.waveCheckInterval = null;
    this.pendingDrag = null; // { pos, gem, startX, startY, started }
  }
  
  start() {
    // Start mana regeneration
    this.manaInterval = setInterval(() => {
      this.store.getState().updateMana();
    }, 100);
    
    // Start elapsed time tracking
    this.timeInterval = setInterval(() => {
      this.store.getState().updateElapsedTime();
    }, 100);
    
    // Check for wave start
    this.waveCheckInterval = setInterval(() => {
      const state = this.store.getState();
      if (state.phase === 'playing' && !state.waveInProgress && state.currentWave < 8) {
        // Auto-start wave after 1 second if not in progress
        this.store.getState().startWave();
      }
    }, 1000);
    
    // Start battle processing
    this.startBattleLoop();
  }
  
  startBattleLoop() {
    const processBattle = () => {
      const state = this.store.getState();
      
      if (state.phase !== 'playing') {
        this.battleInterval = requestAnimationFrame(processBattle);
        return;
      }
      
      // Process battle if wave is in progress
      if (state.waveInProgress) {
        this.store.getState().processBattle();
      }
      
      // Check game over
      this.store.getState().checkGameOver();
      
      // Schedule next frame based on speed multiplier (500ms base = 2 ticks/sec at 1x)
      const delay = 500 / state.speedMultiplier;
      setTimeout(() => {
        this.battleInterval = requestAnimationFrame(processBattle);
      }, delay);
    };
    
    this.battleInterval = requestAnimationFrame(processBattle);
  }
  
  stop() {
    if (this.manaInterval) {
      clearInterval(this.manaInterval);
      this.manaInterval = null;
    }
    
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
      this.timeInterval = null;
    }
    
    if (this.waveCheckInterval) {
      clearInterval(this.waveCheckInterval);
      this.waveCheckInterval = null;
    }
    
    if (this.battleInterval) {
      cancelAnimationFrame(this.battleInterval);
      this.battleInterval = null;
    }
  }
  
  handlePointerDown(event) {
    const state = this.store.getState();
    if (state.phase !== 'playing') return;

    const pos = this.renderer.getCellFromPosition(event.clientX, event.clientY);
    if (!pos) return;

    const gem = state.grid[pos.row][pos.col];
    if (!gem) return;

    // Store pending drag — actual drag starts after threshold movement
    this.pendingDrag = {
      pos,
      gem,
      startX: event.clientX,
      startY: event.clientY,
      started: false
    };

    const handleMove = (e) => {
      if (this.pendingDrag && !this.pendingDrag.started) {
        const dx = e.clientX - this.pendingDrag.startX;
        const dy = e.clientY - this.pendingDrag.startY;
        if (Math.sqrt(dx * dx + dy * dy) >= DRAG_THRESHOLD) {
          // Threshold crossed — begin drag visual
          this.pendingDrag.started = true;
          this.store.getState().startDrag(this.pendingDrag.pos.row, this.pendingDrag.pos.col);
          const dragSprite = this.renderer.createDragSprite(this.pendingDrag.gem);
          const localPos = this.renderer.gridContainer.toLocal({
            x: this.pendingDrag.startX,
            y: this.pendingDrag.startY
          });
          this.renderer.dragOffset.x = localPos.x - dragSprite.x;
          this.renderer.dragOffset.y = localPos.y - dragSprite.y;
        }
      }
      this.handlePointerMove(e);
    };

    const handleUp = (e) => {
      this.pendingDrag = null;
      this.handlePointerUp(e);
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
  }
  
  handlePointerMove(event) {
    const state = this.store.getState();
    if (!state.isDragging) return;
    
    this.renderer.updateDragPosition(event.clientX, event.clientY);
  }
  
  handlePointerUp(event) {
    const state = this.store.getState();
    if (!state.isDragging) return;
    
    const targetCell = this.renderer.getCellFromPosition(event.clientX, event.clientY);
    
    if (targetCell && state.dragStartCell) {
      // Check if dropping on different cell
      if (targetCell.row !== state.dragStartCell.row || targetCell.col !== state.dragStartCell.col) {
        // Attempt merge
        this.store.getState().dropGem(targetCell.row, targetCell.col);
        
        // Check if merge was successful
        const newState = this.store.getState();
        if (!newState.isDragging && newState.gemsMerged > state.gemsMerged) {
          // Merge successful - show effect
          this.showMergeEffect(targetCell);
        } else if (!newState.isDragging) {
          // Merge failed - snap back
          this.renderer.snapBackToOrigin(state.dragStartCell);
        }
      } else {
        // Dropped on same cell - snap back
        this.store.getState().cancelDrag();
        this.renderer.snapBackToOrigin(state.dragStartCell);
      }
    } else {
      // Invalid drop - snap back
      this.store.getState().cancelDrag();
      if (state.dragStartCell) {
        this.renderer.snapBackToOrigin(state.dragStartCell);
      }
    }
  }
  
  showMergeEffect(cell) {
    // Simple visual feedback - could be enhanced
    const graphics = new PIXI.Graphics();
    graphics.beginFill(0xffffff, 0.5);
    graphics.drawCircle(
      cell.col * CELL_SIZE + CELL_SIZE / 2,
      cell.row * CELL_SIZE + CELL_SIZE / 2,
      CELL_SIZE / 2
    );
    graphics.endFill();

    if (this.renderer.gridContainer) {
      this.renderer.gridContainer.addChild(graphics);

      // Fade out
      let alpha = 0.5;
      const fadeOut = () => {
        alpha -= 0.05;
        graphics.alpha = alpha;
        if (alpha > 0) {
          requestAnimationFrame(fadeOut);
        } else {
          if (graphics.parent) {
            this.renderer.gridContainer.removeChild(graphics);
          }
          graphics.destroy();
        }
      };
      fadeOut();
    }
  }
  
  // Simulate a drag from one cell to another (for attract mode bot)
  simulateDrag(fromCell, toCell) {
    const state = this.store.getState();
    if (state.phase !== 'playing') return;
    
    // Get pixel coordinates for cells (global screen coordinates)
    const fromPos = this.renderer.getCellGlobalCenter(fromCell.row, fromCell.col);
    const toPos = this.renderer.getCellGlobalCenter(toCell.row, toCell.col);
    
    // Create synthetic pointer events
    const downEvent = new PointerEvent('pointerdown', {
      clientX: fromPos.x,
      clientY: fromPos.y,
      pointerId: 999,
      pointerType: 'touch'
    });
    
    this.handlePointerDown(downEvent);
    
    // Move after a short delay (to trigger drag start)
    setTimeout(() => {
      const moveEvent = new PointerEvent('pointermove', {
        clientX: toPos.x,
        clientY: toPos.y,
        pointerId: 999,
        pointerType: 'touch'
      });
      this.handlePointerMove(moveEvent);
      
      // Drop after another short delay
      setTimeout(() => {
        const upEvent = new PointerEvent('pointerup', {
          clientX: toPos.x,
          clientY: toPos.y,
          pointerId: 999,
          pointerType: 'touch'
        });
        this.handlePointerUp(upEvent);
      }, 50);
    }, 50);
  }
}
